import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';

/**
 * The agreement, on the collaboration it belongs to.
 *
 * Every term shown here was already scattered across the campaign detail
 * sheet. What changes is that it now has a reference number, a digest, a
 * download and two countersignatures — which is the difference between "the
 * terms are in the app somewhere" and "we have a contract".
 *
 * The PDF is fetched as a blob rather than linked directly: the endpoint is
 * behind a JWT that lives in localStorage, so a plain `<a href>` would arrive
 * unauthenticated. Going through the axios instance also means the token
 * refresh interceptor covers it like every other call.
 */

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const shortDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
}) : '—');

/**
 * Fetch one agreement as a PDF and hand it to the browser as a download.
 *
 * Exported because the contracts drawer downloads the same document from a
 * row that never opens this panel. The blob detour is the point: the endpoint
 * sits behind a JWT in localStorage, so a plain `<a href>` arrives
 * unauthenticated — and going through the axios instance means the refresh
 * interceptor covers it like every other call.
 */
export async function downloadContractPdf(dealId, reference, lang) {
    const res = await api.get(`/api/campaigns/deals/${dealId}/contract/pdf/`, {
        params: { lang, download: 1 },
        responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reference || `contract-${dealId}`}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Released on the next tick — revoking synchronously can beat the click
    // through in Safari and hand the user an empty file.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Term({ label, value }) {
    if (value === null || value === undefined || value === '' || value === 0) return null;
    return (
        <div className="flex items-start justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0">
            <span className="text-[11px] text-slate-500 flex-shrink-0">{label}</span>
            <span className="text-[11px] font-bold text-slate-800 text-right break-words">{value}</span>
        </div>
    );
}

function SignatureSlot({ label, at, isMine, onAccept, busy, t }) {
    return (
        <div className={`rounded-xl border p-2.5 ${at
            ? 'border-emerald-200 bg-emerald-50/60'
            : 'border-slate-200 bg-white'}`}>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide truncate">{label}</p>
            {at ? (
                <p className="text-[11px] font-bold text-emerald-700 mt-0.5">
                    ✓ {shortDate(at)}
                </p>
            ) : isMine ? (
                <button onClick={onAccept} disabled={busy}
                    className="mt-1 w-full py-1.5 rounded-lg text-[11px] font-bold text-primary-700 bg-primary-50 border border-primary-200 hover:bg-primary-100 transition flex items-center justify-center gap-1.5">
                    {busy && <span className="w-3 h-3 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />}
                    {t('contract.acceptBtn')}
                </button>
            ) : (
                <p className="text-[11px] text-slate-400 mt-0.5">{t('contract.awaiting')}</p>
            )}
        </div>
    );
}

/**
 * `defaultOpen` is for the contracts drawer, where the row above has already
 * been expanded on purpose — asking for a second click to reach the terms the
 * user just asked for would be a toll, not a disclosure.
 */
function ContractPanel({ deal, contract: summary, defaultOpen = false, onAccepted }) {
    const { t, lang } = useTranslation();
    const [open, setOpen] = useState(defaultOpen);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');

    const dealId = deal?.id;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/api/campaigns/deals/${dealId}/contract/`,
                { params: { lang } });
            setDetail(res.data);
        } catch (err) {
            setError(t('contract.loadError'));
        } finally { setLoading(false); }
    }, [dealId, lang, t]);

    // Mounted already open (drawer), so nothing will fire the toggle.
    useEffect(() => {
        if (defaultOpen && dealId) load();
    }, [defaultOpen, dealId, load]);

    if (!summary) return null;

    const toggle = () => {
        setOpen(!open);
        if (!open && !detail && !loading) load();
    };

    const download = async () => {
        setDownloading(true);
        setError('');
        try {
            await downloadContractPdf(deal.id, summary.reference, lang);
        } catch (err) {
            setError(t('contract.downloadError'));
        } finally { setDownloading(false); }
    };

    const accept = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await api.post(`/api/campaigns/deals/${deal.id}/contract/`,
                { action: 'accept' });
            setDetail(prev => ({ ...prev, ...res.data }));
            // Lets a list around this panel drop its "needs your signature"
            // badge now rather than at the next page load.
            if (onAccepted) onAccepted();
        } catch (err) {
            setError(t('campaigns.saveError'));
        } finally { setBusy(false); }
    };

    const current = detail || summary;
    const role = detail?.role;
    const snapshot = detail?.snapshot || {};
    const financial = snapshot.financial || {};
    const timeline = snapshot.timeline || {};
    const rights = snapshot.rights || {};
    const parties = snapshot.parties || {};

    return (
        <div className="rounded-2xl border border-primary-200 bg-primary-50/40 overflow-hidden">
            <button onClick={toggle}
                className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-primary-50 transition">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-primary-600 flex-shrink-0"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-800">{t('contract.title')}</p>
                    <p className="text-[11px] text-primary-700 font-semibold tabular-nums truncate">
                        {current.reference}
                    </p>
                </div>
                {current.fully_accepted && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold border border-emerald-200 flex-shrink-0">
                        {t('contract.signed')}
                    </span>
                )}
                <svg xmlns="http://www.w3.org/2000/svg"
                    className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open && (
                <div className="px-4 pb-4 border-t border-primary-100 pt-3 space-y-3">
                    {loading && (
                        <div className="flex justify-center py-4"><span className="spinner-sm" /></div>
                    )}

                    {detail && (
                        <>
                            <p className="text-[11px] text-slate-500 leading-relaxed">
                                {t('contract.frozenNote')}
                            </p>

                            <div className="rounded-xl bg-white border border-slate-200 px-3 py-2">
                                <Term label={t('contract.brand')} value={parties.brand?.username} />
                                <Term label={t('contract.creator')} value={parties.creator?.username} />
                                <Term label={t('contract.amount')} value={usd(financial.amount_usd)} />
                                <Term label={`${t('contract.fee')} (%${financial.platform_fee_percent})`}
                                    value={usd(financial.platform_fee_usd)} />
                                <Term label={t('contract.net')} value={usd(financial.creator_net_usd)} />
                                <Term label={t('contract.draftDue')}
                                    value={timeline.draft_due_date ? shortDate(timeline.draft_due_date) : null} />
                                <Term label={t('contract.publishDate')}
                                    value={timeline.publish_date ? shortDate(timeline.publish_date) : null} />
                                <Term label={t('contract.usageRights')}
                                    value={rights.usage_rights_months
                                        ? `${rights.usage_rights_months} ${t('campaigns.months')}` : null} />
                                <Term label={t('contract.exclusivity')}
                                    value={rights.exclusivity_days
                                        ? `${rights.exclusivity_days} ${t('campaigns.days')}` : null} />
                                <Term label={t('contract.exclusivityNote')} value={rights.exclusivity_note} />
                                <Term label={t('contract.revisionLimit')}
                                    value={snapshot.process?.revision_limit} />
                            </div>

                            {/* Countersignatures */}
                            <div className="grid grid-cols-2 gap-2">
                                <SignatureSlot label={`${t('contract.brand')} · ${parties.brand?.username || ''}`}
                                    at={detail.brand_accepted_at} isMine={role === 'brand'}
                                    onAccept={accept} busy={busy} t={t} />
                                <SignatureSlot label={`${t('contract.creator')} · ${parties.creator?.username || ''}`}
                                    at={detail.creator_accepted_at} isMine={role === 'creator'}
                                    onAccept={accept} busy={busy} t={t} />
                            </div>

                            {/* The digest is what makes a downloaded copy
                                checkable against the record, so it is printed
                                rather than kept in the API only. */}
                            <p className="text-[10px] text-slate-400 break-all leading-relaxed">
                                {t('contract.digest')}: {detail.content_hash}
                            </p>
                        </>
                    )}

                    <button onClick={download} disabled={downloading}
                        className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2">
                        {downloading
                            ? <span className="spinner-sm" />
                            : <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>}
                        {t('contract.downloadBtn')}
                    </button>

                    {error && <p className="text-xs text-red-600 text-center">{error}</p>}
                </div>
            )}
        </div>
    );
}

export default ContractPanel;
